import { Check } from "lucide-react";
import SEO from "@/components/site/SEO";
import PageHeader from "@/components/site/PageHeader";
import FAQSection from "@/components/site/FAQSection";
import QuoteCTA from "@/components/site/QuoteCTA";
import { SERVICES } from "@/lib/site";

const PROCESS = [
  { step: "01", title: "Free site walk", body: "We meet you on-site, measure, and learn how you use the lot." },
  { step: "02", title: "Written quote", body: "Fixed-price, line-item proposal — usually within 48 hours." },
  { step: "03", title: "Scheduled work", body: "After-hours and weekend crews to avoid any business disruption." },
  { step: "04", title: "Final walk-through", body: "We don't leave until you've signed off on every line we painted." },
];

export default function Services() {
  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Parking Lot Services",
    itemListElement: SERVICES.map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "Service",
        name: s.title,
        description: s.description,
        provider: { "@type": "LocalBusiness", name: "901 Lots", "@id": "https://901lots.com/#business" },
        areaServed: "Memphis Metropolitan Area",
      },
    })),
  };

  return (
    <div data-testid="services-page">
      <SEO
        title="Services — Striping, Sealcoating, ADA Compliance & More"
        description="Six core parking lot services from 901 Lots: striping, sealcoating, ADA compliance, restriping, pavement markings, and crack filling. Serving Memphis and the Mid-South."
        path="/services"
        jsonLd={servicesSchema}
      />
      <PageHeader
        eyebrow="Services"
        title="Striping, sealing, and pavement marking — done right."
        subtitle="Six core services covering everything your parking lot needs. Mix and match, or hand us the whole project."
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          {SERVICES.map((s) => (
            <article
              key={s.slug}
              data-testid={`service-detail-${s.slug}`}
              className="bg-white border border-slate-200 rounded-lg p-8 hover:border-blue-300 hover:shadow-md transition-all"
            >
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-orange-600">
                {s.slug.replace(/-/g, " ")}
              </div>
              <h3 className="mt-2 font-display font-bold text-slate-900 text-2xl">
                {s.title}
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                {s.description}
              </p>
              <ul className="mt-5 space-y-2">
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-slate-700">
                    <Check className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-28 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-blue-700">
              Our process
            </div>
            <h2 className="mt-3 font-display font-bold tracking-tight text-slate-900 text-3xl sm:text-4xl">
              Four steps, zero surprises.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p) => (
              <div
                key={p.step}
                data-testid={`process-step-${p.step}`}
                className="bg-white border border-slate-200 rounded-lg p-6"
              >
                <div className="font-display font-black text-blue-700 text-3xl">
                  {p.step}
                </div>
                <h3 className="mt-3 font-display font-semibold text-slate-900 text-lg">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQSection
        title="Service questions, answered"
        eyebrow="FAQ"
        subtitle="Quick answers about timing, materials, pricing, and how we work."
        limit={6}
        showSeeAllLink
        background="white"
      />

      <QuoteCTA />
    </div>
  );
}
